import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangProvider } from "@/context/LangContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";

import Nav from "@/components/umaang/Nav";
import InstallPrompt from "@/components/umaang/InstallPrompt";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Home from "@/pages/Home";
import ProfileDetail from "@/pages/ProfileDetail";
import Messages from "@/pages/Messages";
import Insights from "@/pages/Insights";
import Premium from "@/pages/Premium";
import Admin from "@/pages/Admin";
import AdminUserEdit from "@/pages/AdminUserEdit";
import InstallApp from "@/pages/InstallApp";
import EditProfile from "@/pages/EditProfile";
import Referrals from "@/pages/Referrals";
import KycComplete from "@/pages/KycComplete";
import Inbox from "@/pages/Inbox";
import AuthCallback from "@/pages/AuthCallback";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Quiz from "@/pages/Quiz";
import AIAssistant from "@/pages/AIAssistant";
import { ForgotPassword, ResetPassword, VerifyEmail } from "@/pages/AuthExtras";

import "@/App.css";

function Protected({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen grid place-items-center" data-testid="loading-state">
        <div className="text-[color:var(--umg-text-2)]">Loading…</div>
      </div>
    );
  if (!user) return <Navigate to="/auth" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/home" replace />;
  return children;
}

function Shell() {
  return (
    <div className="App min-h-screen grain-overlay" style={{ background: "var(--umg-bg)", color: "var(--umg-text)" }}>
      <Nav />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/install" element={<InstallApp />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/family" element={<Navigate to="/home" replace />} />
        <Route path="/family/:id" element={<Navigate to="/home" replace />} />
        <Route path="/quiz" element={<Protected><Quiz /></Protected>} />
        <Route path="/ai-assistant" element={<Protected><AIAssistant /></Protected>} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/home" element={<Protected><Home /></Protected>} />
        <Route path="/profile/edit" element={<Protected><EditProfile /></Protected>} />
        <Route path="/profile/:id" element={<Protected><ProfileDetail /></Protected>} />
        <Route path="/compare/:id" element={<Navigate to="/home" replace />} />
        <Route path="/messages" element={<Navigate to="/inbox?tab=threads" replace />} />
        <Route path="/messages/:id" element={<Protected><Messages /></Protected>} />
        <Route path="/insights" element={<Protected><Insights /></Protected>} />
        <Route path="/premium" element={<Protected><Premium /></Protected>} />
        <Route path="/referrals" element={<Protected><Referrals /></Protected>} />
        <Route path="/kyc/complete" element={<Protected><KycComplete /></Protected>} />
        <Route path="/inbox" element={<Protected><Inbox /></Protected>} />
        <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
        <Route path="/admin/users/:id/edit" element={<Protected adminOnly><AdminUserEdit /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <InstallPrompt />
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LangProvider>
          <AuthProvider>
            <Shell />
          </AuthProvider>
        </LangProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
